import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { fullscreenCardAnimation, cardFeedAnimation, modelFeedAnimation, homeOverscrollAnimation, navbarmodelFeedAnimation, inOutAnimation } from 'src/app/shared/providers/animations-list';
import { WebModelsCardComponent } from '../web-models-card/web-models-card.component';
import { WebnavComponent } from '../webnav/webnav.component';
import { CARD_CONTROL_BTN, CARDS_MODE, Cards_Page_State, CARD_PURCHASE_STATUS, ICard, ICardAction, ICardFeedModificator, INavpanelAction, ModelCardRendered, ModelCardRenderedSelection, NAVPANEL_ACTION_TYPE } from 'src/app/models/card';
import { FOOTER_MODE, NAVBAR_BTN } from 'src/app/models/menu';
import { Subscription } from 'rxjs';
import { ICardCollection } from 'src/app/models/collection';
import { CardsService } from 'src/app/shared/providers/cards.service';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import { UserDataService } from 'src/app/shared/providers/user-data.service';
import { UtilityFunctionsService } from 'src/app/shared/providers/utility-functions.service';
import { ReviewCollectionsService } from 'src/app/shared/providers/review-collections.service';
import { WebNavService } from '../webnavigation.service';
import { LOCALSTORAGE_VAR } from 'src/app/models/localstorage';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent, DialogData } from 'src/app/shared/components/dialog/confirmation-dialog.component';
import { POPUP_TEXT } from 'src/app/shared/text/popup-texts';

@Component({
  selector: 'app-web-store',
  templateUrl: './web-store.component.html',
  styleUrls: ['./web-store.component.scss'],
  animations: [
    fullscreenCardAnimation,
    cardFeedAnimation,
    modelFeedAnimation,
    homeOverscrollAnimation,
    navbarmodelFeedAnimation,
    inOutAnimation
  ],
})
export class WebStoreComponent implements OnInit, OnDestroy {
  @ViewChild('modelCardsFeed', { static: false })
  public modelCardsFeedRef: WebModelsCardComponent;

  @ViewChild('navbarComponent', { static: false })
  public webnav: WebnavComponent;

  public currentAnimationState: string = Cards_Page_State.CARD_FEED;
  public CARDS_MODE = CARDS_MODE;
  public FOOTER_MODE = FOOTER_MODE;
  public readonly storeFeedModificator: ICardFeedModificator = { isStoreFeed: true };

  public isNoDataRecieved: boolean = false;
  public errorData: unknown;
  public collectionDetailsSource: ICardCollection;

  public isModelFeedVisible: boolean = false;
  public modelFeedCards: ModelCardRendered[] = [];
  public modelFeedCardStartPosition: number = 0;

  public isCollectionDetailsShown: boolean = false;
  public currentCard: ICard | null = null;

  public isPublishBulkShown: boolean = false;
  public cardSelectData: ModelCardRenderedSelection[] = [];

  private modelIdChangeSubscription: Subscription;
  private routeModelId: string | null = null;

  private isComponentActive: boolean = true;
  private activeLoadToken: number = 0;

  constructor(
    private router: Router,
    private readonly route: ActivatedRoute,
    public webNavService: WebNavService,
    public firebaseAuth: FirebaseAuthService,
    public cardService: CardsService,
    public reviewCollectionsService: ReviewCollectionsService,
    public userDataService: UserDataService,
    public utilityService: UtilityFunctionsService,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    this.webNavService.footerMode = FOOTER_MODE.STORE;
    this.modelIdChangeSubscription = this.route.queryParamMap.subscribe((queryParams) => {
      this.routeModelId = queryParams.get('modelId');
      this.tryOpenModelFromQueryParam();
    });

    try {
      await this.reloadPublishedReviewsCollection();
    } catch (error) {
      this.utilityService.rollbarError(
        `User ${localStorage.getItem(LOCALSTORAGE_VAR.ORIGINAL_UID)}, Error fetching published reviews: ${error}`
      );
      this.router.navigate(['/']);
      this.dialog.open<ConfirmationDialogComponent, DialogData>(ConfirmationDialogComponent, {
        width: '370px',
        data: this.utilityService.applyPopupText(POPUP_TEXT.SHARED_CONTENT_NOT_AVAILABLE, '{contentType}', 'Collection'),
      });
    }

  }

  private async reloadPublishedReviewsCollection(options: { restoreFromQueryParam?: boolean } = {}): Promise<void> {
    const loadToken = ++this.activeLoadToken;
    const reviewCollection = await this.reviewCollectionsService.getPublicStoreCollection();
    if (!this.isLoadTokenActive(loadToken)) return;
    this.applyPublishedReviewsCollection(reviewCollection);
    if (options.restoreFromQueryParam !== false) {
      this.tryOpenModelFromQueryParam();
    }
  }

  private applyPublishedReviewsCollection(sourceCollection: ICardCollection): void {
    if (!this.isComponentActive) return;
    this.collectionDetailsSource = this.patchModelsPurchaseStatusToDefault(sourceCollection);
    const collectionCard = this.cardService.convertCollectionsToCardsFeed([this.collectionDetailsSource])[0];
    if (!collectionCard) return;
    this.cardService.setCurrentCard(collectionCard);
    this.applyMoreState(collectionCard);
    this.utilityService.rollbarInfo(`Published reviews collection opened, userUid: ${this.firebaseAuth.currentUser?.uid}`)
  }

  private patchModelsPurchaseStatusToDefault(sourceCollection: ICardCollection): ICardCollection {
    const patchedCards = (sourceCollection?.collectionCards || []).map((card) => ({
      ...card,
      purchaseStatus: CARD_PURCHASE_STATUS.DEFAULT_3DWAY,
    })) as ModelCardRendered[];

    return {
      ...sourceCollection,
      collectionCards: patchedCards,
    };
  }

  private tryOpenModelFromQueryParam(): void {
    if (!this.routeModelId || !this.collectionDetailsSource?.collectionCards?.length) return;

    const targetIndex = this.findCollectionCardIndexByModelIdentifier(this.routeModelId);
    if (targetIndex < 0) return;

    if (this.isModelFeedVisible && this.modelFeedCardStartPosition === targetIndex) return;

    this.onCollectionDetailsThumbClick(targetIndex);
  }

  private findCollectionCardIndexByModelIdentifier(modelIdentifier: string): number {
    const normalizedRequestedModelIdentifier = this.normalizeModelIdentifier(modelIdentifier);
    if (!normalizedRequestedModelIdentifier) return -1;

    return this.collectionDetailsSource.collectionCards.findIndex((card) => {
      const normalizedCardId = this.normalizeModelIdentifier(card.id);
      const normalizedPublicModelId = this.normalizeModelIdentifier(card.publicModelId);

      return (
        normalizedRequestedModelIdentifier === normalizedCardId ||
        normalizedRequestedModelIdentifier === normalizedPublicModelId
      );
    });
  }

  private normalizeModelIdentifier(modelIdentifier?: string | null): string {
    if (!modelIdentifier) return '';
    return this.safeDecodeURIComponent(modelIdentifier).trim().toLowerCase();
  }

  private safeDecodeURIComponent(value: string): string {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  }

  private updateModelIdQueryParam(modelId: string | null): void {
    this.routeModelId = modelId;
    const queryParams: Params = modelId
      ? { modelId }
      : { modelId: null };
    this.router.navigate([], {
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    }).catch((err) => {
      console.error('Error updating modelId query param:', err);
    });
  }

  private applyMoreState(currentCard: ModelCardRendered): void {
    if (!this.collectionDetailsSource?.collectionCards?.length) return;
    this.currentCard = currentCard;
    this.cardService.collectionDetailsInitTimeStart = performance.now();
    this.modelFeedCards = this.collectionDetailsSource.collectionCards;
    this.cardService.setLastCollectionCard(currentCard);
    this.isCollectionDetailsShown = true;
    if (!this.isPublishBulkShown) {
      this.webNavService.footerMode = FOOTER_MODE.STORE;
    }
  }

  public handleWebpanelClick(action: INavpanelAction) {
    const actionName = action.actionType
    switch (true) {
      case actionName === NAVPANEL_ACTION_TYPE.LIKE_COLLECTION:
        // DIRTY HACK: Because updates of navpanel items are very quickly and before navpanel hidden you see different variant;
         setTimeout(() => {
          this.toggleCollectionLike();
         }, 250);
        break;
    }
  }

  public onNavbarBtnClicked(navbarBtn: NAVBAR_BTN) {
    console.log('clicked ', navbarBtn)
    switch (navbarBtn) {
      case NAVBAR_BTN.HOME:
        this.router.navigate(['/']);
        break;
      case NAVBAR_BTN.LIBRARY:
        this.invalidateActiveLoad();
        this.router.navigate(['/library']);
        break;
      case NAVBAR_BTN.PUBLISH:
        if (!this.firebaseAuth.currentUser) {
          this.webnav?.handleUnauthorizedUserActionDetected();
          break;
        }
        if (!this.isPublishBulkShown) {
          this.openPublishBulkMode();
        } else {
          this.publishSelectedModelsBulk();
        }
        break;
      case NAVBAR_BTN.SEARCH:
        console.log('not implemented')
        break;
      case NAVBAR_BTN.BUY:
        console.log('BUY BTN CLICKED');
        break;
      case NAVBAR_BTN.BACK:
        if (this.isPublishBulkShown) {
          this.closePublishBulkMode();
          break;
        }
        this.utilityService.notImplemented();
        break;
      case NAVBAR_BTN.SELECT_ALL:
        this.utilityService.notImplemented();
        break;
      case NAVBAR_BTN.MORE_OPTIONS:
        this.utilityService.notImplemented();
        break;
      case NAVBAR_BTN.NOT_IMPL:
        this.utilityService.notImplemented();
        break;
      default:
        break;
    }
  }

  public onCardVisibilityChanged(card: ModelCardRendered) {
    if (this.isPublishBulkShown) return;
    this.webNavService.footerMode = this.isModelFeedVisible ? FOOTER_MODE.DEFAULT : FOOTER_MODE.STORE;
  }

  public onCollectionDetailsThumbClick(thumbNumber: number): void {
    this.modelFeedCards = this.collectionDetailsSource.collectionCards;
    console.log('onCollectionDetailsThumbClick, this.modelFeedCards: ', this.modelFeedCards)

    this.cardService.modelFeedInitStart = performance.now();

    this.modelFeedCardStartPosition = thumbNumber;
    this.webNavService.footerMode = FOOTER_MODE.DEFAULT;
    this.isModelFeedVisible = true;
  }

  public onStoreThumbClicked(card: ModelCardRendered): void {
    if (!card || !this.collectionDetailsSource?.collectionCards?.length) return;
    const thumbIndex = this.collectionDetailsSource.collectionCards.findIndex(item => item.id === card.id);
    if (thumbIndex < 0) return;
    this.updateModelIdQueryParam(card.id);
    this.onCollectionDetailsThumbClick(thumbIndex);
  }

  public handleNavpanelAction(action: INavpanelAction): void {
    const actionName = action.actionType
    switch (true) {
      case actionName === NAVPANEL_ACTION_TYPE.LIKE_COLLECTION:
          this.toggleCollectionLike();
        break;
      case actionName === NAVPANEL_ACTION_TYPE.SHARE_COLLECTION:
        this.utilityService.notImplemented();
        break;
      case actionName === NAVPANEL_ACTION_TYPE.SHARE_MODEL:
        this.modelCardsFeedRef.onShareBtnClicked(this.cardService.getCurrentCard());
        break;
      case actionName === NAVPANEL_ACTION_TYPE.UNLIKE_CARD:
        this.modelCardsFeedRef.dispatchCurrentCardLikeClick();
        break;
      default:
        break;
    }
  }

  private toggleCollectionLike(): void {
    if (!this.webnav) return;
    this.webnav.onCollectionLikeClicked(new Event('click'));
  }


  public onCardCollectionControlBtnClicked(action: ICardAction): void {
    console.log('onCardCollectionControlBtnClicked: ', action)
    this.utilityService.triggerHaptics();
    switch (action.controlAction) {
      case CARD_CONTROL_BTN.ANONYMOUS_USER:
        this.webnav.handleUnauthorizedUserActionDetected();
        break;
      case CARD_CONTROL_BTN.CONTROL_ACTIONS_DEFAULT:
      case CARD_CONTROL_BTN.CONTROL_ACTION_FOR_LIKED:
        this.utilityService.notImplemented();
        break;
      case CARD_CONTROL_BTN.UNLIKE_DRAFT_CARD:
        this.modelCardsFeedRef.dispatchCurrentCardLikeClick();
        break;
      case CARD_CONTROL_BTN.CONTROL_GO_TO_DETAILS:
        if (!this.isModelFeedVisible) {
          this.currentCard = action.card;
          this.cardService.triggerMoreBtnSubject();
        }
        break;
      case CARD_CONTROL_BTN.CONTROL_BACK_BTN:
          if (!this.currentCard) return;
          this.webNavService.footerMode = FOOTER_MODE.STORE;
          this.cardService.setCurrentCard(this.currentCard);
          this.updateModelIdQueryParam(null);
          this.isModelFeedVisible = false;
          // this.navbarAnimationState = 'default';
        break
      case CARD_CONTROL_BTN.SEARCH:
        this.utilityService.showImportantSnackBar('Coming soon', 250, 750);
        break;
      case CARD_CONTROL_BTN.BUY_MODEL:
        this.utilityService.showImportantSnackBar('Coming soon', 250, 750);
        break;
      case CARD_CONTROL_BTN.CANCEL_PUBLISH:
        this.openCancelPublishingCardConfirmationDialog(action.card);
        break;
      default:
        break;
    }
  }

  private openCancelPublishingCardConfirmationDialog(card: ICard): void {
    if (!card) return;
    const dialogRef = this.dialog.open<ConfirmationDialogComponent, DialogData>(ConfirmationDialogComponent, {
      data: this.utilityService.applyPopupText(POPUP_TEXT.CANCEL_PUBLISHING_NO_TITLE, '{modelTitle}', card.cardTitle)
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result !== true) return;
      await this.rejectPublishedModelAndRefresh(card);
    });
  }

  // For admin only
  private async rejectPublishedModelAndRefresh(card: ICard): Promise<void> {
    try {
      await this.cardService.rejectReviewModelPutRequest(card);
      this.utilityService.showSnackBar('Model rejected successfully.', 500);
      this.updateModelIdQueryParam(null);
      this.isModelFeedVisible = false;
      this.webNavService.footerMode = FOOTER_MODE.STORE;
      await this.reloadPublishedReviewsCollection({ restoreFromQueryParam: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
      this.utilityService.showErrorSnackBar(`Error occured on rejecting review: ${errorMessage}`, 500);
    }
  }


  public onPublishBulkSelected(selectedCards: ModelCardRenderedSelection[]): void {
    this.userDataService.changeStateIsNavbarMainActionDisabledTo(selectedCards.length === 0);
  }

  private isCardAvailableForPublishBulk(card: ModelCardRenderedSelection): boolean {
    if (!card) return false;
    return card.purchaseStatus !== CARD_PURCHASE_STATUS.IN_REVIEW
      && card.purchaseStatus !== CARD_PURCHASE_STATUS.PUBLISHED
      && card.purchaseStatus !== CARD_PURCHASE_STATUS.REJECTED
      && card.purchaseStatus !== CARD_PURCHASE_STATUS.REVIEW_PUBLISH_REJECT;
  }

  private getCardsForPublishBulk(): ModelCardRenderedSelection[] {
    const byId = new Map<string, ModelCardRenderedSelection>();

    const mergeCard = (card: ICard): void => {
      if (!card?.id) return;
      const existing = byId.get(card.id);
      if (!existing) {
        byId.set(card.id, card as ModelCardRenderedSelection);
        return;
      }
      if (existing.purchaseStatus !== CARD_PURCHASE_STATUS.UPLOADED && card.purchaseStatus === CARD_PURCHASE_STATUS.UPLOADED) {
        byId.set(card.id, card as ModelCardRenderedSelection);
      }
    };

    this.userDataService.uploadedCards.forEach(mergeCard);
    this.userDataService.userReviewPublishRejectModels.forEach(mergeCard);

    return Array.from(byId.values());
  }

  private openPublishBulkMode(): void {
    this.cardSelectData = this.getCardsForPublishBulk();
    this.cardSelectData.forEach(card => {
      card.isSelected = false;
      card.isDisabled = !this.isCardAvailableForPublishBulk(card);
    });
    this.isModelFeedVisible = false;
    this.isPublishBulkShown = true;
    this.webNavService.footerMode = FOOTER_MODE.PUBLISH_SELECTED_MODELS;
    this.userDataService.changeStateIsNavbarMainActionDisabledTo(true);
  }

  private clearPublishBulkSelectionState(): void {
    this.cardSelectData?.forEach(card => {
      card.isSelected = false;
      card.isDisabled = undefined;
    });
  }

  private closePublishBulkMode(): void {
    this.clearPublishBulkSelectionState();
    this.isPublishBulkShown = false;
    this.webNavService.footerMode = FOOTER_MODE.STORE;
    this.userDataService.changeStateIsNavbarMainActionDisabledTo(false);
  }

  private publishSelectedModelsBulk(): void {
    const selectedCardsToPublish = (this.cardSelectData || [])
      .filter(card => card.isSelected && this.isCardAvailableForPublishBulk(card));

    if (selectedCardsToPublish.length === 0) {
      this.userDataService.changeStateIsNavbarMainActionDisabledTo(true);
      return;
    }

    this.closePublishBulkMode();
    this.userDataService.sendModelsForReview(selectedCardsToPublish);
  }

  ngOnDestroy(): void {
    this.invalidateActiveLoad();
    this.modelIdChangeSubscription?.unsubscribe();
  }

  private invalidateActiveLoad(): void {
    this.isComponentActive = false;
    this.activeLoadToken += 1;
  }

  private isLoadTokenActive(loadToken: number): boolean {
    return this.isComponentActive && loadToken === this.activeLoadToken;
  }


}
